// src/pages/Quiz.jsx
//
// Quiz-Tablet (Rolle "quiz", ohne Seitenleiste – siehe App.js). Ein Team
// scannt seinen Laufzettel, liest die Regeln, startet selbst und beantwortet
// die Fragen gegen die Uhr. Ausgewertet wird auf dem Server (routes/quiz.js);
// die Loesungen kommen nie auf das Tablet.
//
// Robustheit:
//  - Der laufende Versuch liegt in localStorage. Neu laden, versehentlich
//    zurueck, abgestuerzter Browser: es geht mit derselben Restzeit weiter.
//  - Antworten gehen laufend als Zwischenstand an den Server. Faellt das
//    Tablet ganz aus, kann der Admin den Versuch damit werten.
//  - Das Ergebnis wird so lange gesendet, bis der Server es bestaetigt.
//  - Ein zweiter Versuch ist serverseitig ausgeschlossen.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Settings, QrCode, Users, Maximize, LogOut, RefreshCw, Loader2, Timer, SkipForward, Volume2, VolumeX, X } from "lucide-react";
import TeamQrScanner from "../components/TeamQrScanner";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { audioFreischalten, klingeln } from "../utils/klingeln";
import {
  vorlesen,
  vorlesenStoppen,
  vorlesenFreischalten,
  vorlesetext,
  vorlesenVerfuegbar,
  vorlesenTesten,
  vorlesenAus,
  TEMPI,
  tempoSchritt,
  tempoLabel,
  deutscheStimmen,
  beiStimmenGeladen,
  geraeteEinstellungen,
  speichereGeraeteEinstellungen,
} from "../utils/vorlesen";
import useWakeLock from "../hooks/useWakeLock";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SPEICHER_STATION = "quiz_tablet_station";
const SPEICHER_LAUF = "quiz_tablet_lauf";
// Sperre nach jedem Antippen: ein Doppeltipp soll nicht die naechste Frage
// gleich mit beantworten.
const TIPP_SPERRE_MS = 350;
const ZWISCHENSTAND_ABSTAND_MS = 4000;
const SENDEN_WIEDERHOLEN_MS = 5000;
const ZURUECK_NACH_MS = 45000;
const ENDSPURT_MS = 10000;

const BUCHSTABEN = ["A", "B", "C", "D"];

function lies(schluessel) {
  try {
    const roh = localStorage.getItem(schluessel);
    return roh ? JSON.parse(roh) : null;
  } catch {
    return null;
  }
}
function schreib(schluessel, wert) {
  try {
    if (wert === null || wert === undefined) localStorage.removeItem(schluessel);
    else localStorage.setItem(schluessel, JSON.stringify(wert));
  } catch {
    // Speicher voll/gesperrt: das Quiz laeuft trotzdem, nur ohne Absturzschutz.
  }
}

const post = (pfad, body) =>
  fetchWithAuth(`${BACKEND_URL}${pfad}`, null, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });

function uhr(ms) {
  const gesamt = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(gesamt / 60)}:${String(gesamt % 60).padStart(2, "0")}`;
}

function dauerText(sekunden) {
  const m = Math.floor(sekunden / 60);
  const s = sekunden % 60;
  if (!m) return `${s} Sekunden`;
  return s ? `${m}:${String(s).padStart(2, "0")} Minuten` : `${m} ${m === 1 ? "Minute" : "Minuten"}`;
}

const punkteText = (p) => (Number.isInteger(p) ? String(p) : String(p).replace(".", ","));

/** Erste Frage ohne Antwort (uebersprungen = null zaehlt als beantwortet). */
const naechsteFrage = (fragen, antworten) => fragen.findIndex((f) => !(f.id in antworten));

// ---------------------------------------------------------------------------

function PinDialog({ open, titel, onClose, onOk, fehler, laedt }) {
  const [pin, setPin] = useState("");
  useEffect(() => {
    if (open) setPin("");
  }, [open]);
  const taste = (z) => setPin((p) => (p.length < 8 ? p + z : p));
  return (
    <Modal open={open} onClose={onClose} title={titel} className="max-w-sm">
      <div className="text-center text-4xl tracking-[0.5em] font-mono h-12">{"•".repeat(pin.length)}</div>
      {fehler && <div className="p-2 rounded bg-red-800 text-red-100 text-sm text-center">{fehler}</div>}
      <div className="grid grid-cols-3 gap-2">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((z) => (
          <button key={z} onClick={() => taste(z)} className="py-4 rounded-lg bg-secondary-700 text-2xl font-semibold active:bg-secondary-600">
            {z}
          </button>
        ))}
        <button onClick={() => setPin("")} className="py-4 rounded-lg bg-secondary-800 text-base">Löschen</button>
        <button onClick={() => taste("0")} className="py-4 rounded-lg bg-secondary-700 text-2xl font-semibold active:bg-secondary-600">0</button>
        <button
          onClick={() => onOk(pin)}
          disabled={pin.length < 4 || laedt}
          className="py-4 rounded-lg bg-yellow-400 text-black text-lg font-bold disabled:opacity-50"
        >
          {laedt ? <Loader2 className="animate-spin mx-auto" /> : "OK"}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------

export default function Quiz() {
  const { logout, user } = useAuth();
  const [stationen, setStationen] = useState(null);
  const [stationNr, setStationNr] = useState(() => lies(SPEICHER_STATION));
  const [ladeFehler, setLadeFehler] = useState("");

  // Phasen: bereit | team | laeuft | senden | fertig
  const [phase, setPhase] = useState("bereit");
  const [meldung, setMeldung] = useState("");
  const [arbeitet, setArbeitet] = useState(false);
  const [anmeldung, setAnmeldung] = useState(null); // Antwort von /anmelden
  const [lauf, setLauf] = useState(null); // { station, ticket, team, fragen, antworten, ende_ms, abzug }
  const [ergebnis, setErgebnis] = useState(null);
  const [jetzt, setJetzt] = useState(Date.now());

  const [scanner, setScanner] = useState(false);
  const [pinZweck, setPinZweck] = useState(null); // "team" | "einstellungen"
  const [pinFehler, setPinFehler] = useState("");
  const [pinLaedt, setPinLaedt] = useState(false);
  const [teamListe, setTeamListe] = useState(null); // { pin, teams }
  const [teamSuche, setTeamSuche] = useState("");
  const [einstellungen, setEinstellungen] = useState(false);
  const [vorlesenProbe, setVorlesenProbe] = useState(null); // { ok, text } von "Vorlesen testen"
  // Stimme und Sprechtempo: Einstellung DIESES Geraets (localStorage), je
  // Kategorie ein Tempo. Aenderbar in den Einstellungen und waehrend des Quiz.
  const [geraet, setGeraet] = useState(geraeteEinstellungen);
  const [stimmen, setStimmen] = useState(() => deutscheStimmen());
  const [beendenFragen, setBeendenFragen] = useState(false);

  const tippSperre = useRef(0);
  const zwischenstand = useRef({ zuletzt: 0, timer: null });
  const klingelStop = useRef(null);

  useWakeLock(true);

  const station = useMemo(
    () => (stationen || []).find((s) => s.station_number === stationNr) || null,
    [stationen, stationNr]
  );

  // --- Stationen laden ------------------------------------------------------
  const stationenLaden = useCallback(async () => {
    setLadeFehler("");
    try {
      const liste = await fetchWithAuth(`${BACKEND_URL}/quiz/stationen`, null);
      setStationen(liste);
      // Genau eine Quiz-Station: ohne Nachfrage nehmen.
      setStationNr((bisher) => {
        if (liste.some((s) => s.station_number === bisher)) return bisher;
        return liste.length === 1 ? liste[0].station_number : null;
      });
    } catch (err) {
      setLadeFehler(err.message || "Stationen konnten nicht geladen werden.");
    }
  }, []);

  useEffect(() => {
    stationenLaden();
  }, [stationenLaden]);

  useEffect(() => {
    schreib(SPEICHER_STATION, stationNr);
  }, [stationNr]);

  // --- Laufenden Versuch nach Neuladen fortsetzen ---------------------------
  useEffect(() => {
    const gespeichert = lies(SPEICHER_LAUF);
    if (gespeichert && gespeichert.ticket && Array.isArray(gespeichert.fragen)) {
      setLauf(gespeichert);
      setPhase(gespeichert.phase === "senden" ? "senden" : "laeuft");
    }
  }, []);

  useEffect(() => {
    if (lauf && (phase === "laeuft" || phase === "senden")) schreib(SPEICHER_LAUF, { ...lauf, phase });
  }, [lauf, phase]);

  // --- Uhr --------------------------------------------------------------------
  useEffect(() => {
    if (phase !== "laeuft") return undefined;
    const t = setInterval(() => setJetzt(Date.now()), 200);
    return () => clearInterval(t);
  }, [phase]);

  const restMs = lauf ? lauf.ende_ms - jetzt : 0;

  const zurueckZumStart = useCallback(() => {
    if (klingelStop.current) klingelStop.current();
    klingelStop.current = null;
    schreib(SPEICHER_LAUF, null);
    setLauf(null);
    setAnmeldung(null);
    setErgebnis(null);
    setMeldung("");
    setPhase("bereit");
  }, []);

  // --- Zwischenstand ----------------------------------------------------------
  const zwischenstandSenden = useCallback((aktuell) => {
    const z = zwischenstand.current;
    if (z.timer) return; // schon geplant – nimmt dann den neuesten Stand
    const warte = Math.max(0, z.zuletzt + ZWISCHENSTAND_ABSTAND_MS - Date.now());
    z.timer = setTimeout(() => {
      z.timer = null;
      z.zuletzt = Date.now();
      const stand = aktuell.current;
      if (!stand) return;
      post(`/quiz/${stand.station}/zwischenstand`, { ticket: stand.ticket, antworten: stand.antworten }).catch(() => {
        // Egal: der Abschluss schickt ohnehin alle Antworten.
      });
    }, warte);
  }, []);

  const laufRef = useRef(null);
  laufRef.current = lauf;

  useEffect(
    () => () => {
      if (zwischenstand.current.timer) clearTimeout(zwischenstand.current.timer);
    },
    []
  );

  // --- Abschluss senden (mit Wiederholung) -----------------------------------
  useEffect(() => {
    if (phase !== "senden" || !lauf) return undefined;
    let abgebrochen = false;
    let timer = null;
    const senden = async () => {
      try {
        const r = await post(`/quiz/${lauf.station}/abschluss`, { ticket: lauf.ticket, antworten: lauf.antworten });
        if (abgebrochen) return;
        schreib(SPEICHER_LAUF, null);
        setErgebnis({
          ...r.ergebnis,
          team: lauf.team,
          zeitAbgelaufen: lauf.ende_ms <= Date.now(),
          beendet: lauf.beendet === true,
        });
        setPhase("fertig");
        setMeldung("");
      } catch (err) {
        if (abgebrochen) return;
        // 400/404: Ticket oder Versuch ungueltig – Wiederholen hilft nicht.
        if (err.status === 400 || err.status === 404) {
          setMeldung(`${err.message} Bitte die Orga holen – die Antworten sind auf dem Server gesichert.`);
          return;
        }
        setMeldung(`Ergebnis noch nicht übertragen (${err.message}). Neuer Versuch läuft automatisch …`);
        timer = setTimeout(senden, SENDEN_WIEDERHOLEN_MS);
      }
    };
    senden();
    return () => {
      abgebrochen = true;
      if (timer) clearTimeout(timer);
    };
    // Nur beim Eintritt in die Phase – `lauf` aendert sich dort nicht mehr.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // --- Zeit abgelaufen / alles beantwortet -----------------------------------
  useEffect(() => {
    if (phase !== "laeuft" || !lauf) return;
    const fertig = naechsteFrage(lauf.fragen, lauf.antworten) === -1;
    if (restMs <= 0 || fertig) {
      if (restMs <= 0) klingelStop.current = klingeln();
      setPhase("senden");
    }
  }, [phase, lauf, restMs]);

  // --- Vorlesen ---------------------------------------------------------------
  // Jede neue Frage wird vorgelesen; der Countdown laeuft dabei weiter. Mit
  // der Antwort wechselt die Frage und das Vorlesen bricht ab (bzw. beginnt
  // die naechste). Ausserhalb des Laufs ist sofort Ruhe.
  const aktuelleFrage =
    phase === "laeuft" && lauf ? lauf.fragen[naechsteFrage(lauf.fragen, lauf.antworten)] || null : null;
  // Stumm ist eine Geraete-Einstellung: der Betreuer kann das Vorlesen am
  // Tablet jederzeit ausschalten (und wieder an).
  const vorlesenAn = phase === "laeuft" && lauf?.vorlesen === true && !geraet.stumm;
  const tempoSchluessel = (kategorie) => (kategorie === "Kinder" ? "kinder" : "erwachsene");
  const laufTempo = geraet.tempo[tempoSchluessel(lauf?.team?.event_type)];
  const geraetAendern = (neu) => {
    setGeraet(neu);
    speichereGeraeteEinstellungen(neu);
  };
  // Tempo fuer eine Kategorie setzen; laeuft gerade ein Quiz, die aktuelle
  // Frage gleich im neuen Tempo noch einmal vorlesen.
  const tempoSetzen = (kategorie, wert, neuVorlesen) => {
    geraetAendern({ ...geraet, tempo: { ...geraet.tempo, [tempoSchluessel(kategorie)]: wert } });
    if (neuVorlesen && aktuelleFrage) vorlesen(vorlesetext(aktuelleFrage), { tempo: wert });
  };
  useEffect(() => {
    if (vorlesenAn && aktuelleFrage) vorlesen(vorlesetext(aktuelleFrage), { tempo: laufTempo });
    else vorlesenStoppen();
    // Nur bei Fragewechsel – nicht bei jedem Uhr-Tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vorlesenAn, aktuelleFrage?.id]);
  useEffect(() => () => vorlesenAus(), []);
  // Chrome/Android liefern die Stimmenliste erst nachtraeglich.
  useEffect(() => beiStimmenGeladen(() => setStimmen(deutscheStimmen())), []);

  // Ergebnisseite raeumt sich selbst ab.
  useEffect(() => {
    if (phase !== "fertig") return undefined;
    const t = setTimeout(zurueckZumStart, ZURUECK_NACH_MS);
    return () => clearTimeout(t);
  }, [phase, zurueckZumStart]);

  // --- Anmelden ---------------------------------------------------------------
  const anmelden = async (body) => {
    if (!station) return;
    setArbeitet(true);
    setMeldung("");
    try {
      const r = await post(`/quiz/${station.station_number}/anmelden`, body);
      setAnmeldung(r);
      setPhase("team");
    } catch (err) {
      setMeldung(err.message || "Anmeldung fehlgeschlagen.");
    } finally {
      setArbeitet(false);
    }
  };

  const onScan = (text) => {
    setScanner(false);
    anmelden({ share_token: String(text) });
  };

  const starten = async () => {
    if (!anmeldung?.ticket) return;
    audioFreischalten(); // im Klick, sonst darf das Tablet spaeter nicht klingeln
    if (anmeldung.vorlesen) vorlesenFreischalten(); // ebenso fuer die Sprachausgabe
    setArbeitet(true);
    setMeldung("");
    try {
      const r = await post(`/quiz/${station.station_number}/start`, { ticket: anmeldung.ticket });
      const neu = {
        station: station.station_number,
        ticket: anmeldung.ticket,
        team: anmeldung.team,
        fragen: r.fragen,
        antworten: r.antworten || {},
        ende_ms: Date.now() + r.rest_ms,
        abzug: r.abzug,
        vorlesen: r.vorlesen === true,
      };
      zwischenstand.current.zuletzt = Date.now();
      tippSperre.current = Date.now() + TIPP_SPERRE_MS;
      setLauf(neu);
      setJetzt(Date.now());
      setPhase(r.rest_ms > 0 ? "laeuft" : "senden");
    } catch (err) {
      setMeldung(err.message || "Start fehlgeschlagen.");
    } finally {
      setArbeitet(false);
    }
  };

  const antworten = (frageId, wert) => {
    if (Date.now() < tippSperre.current || phase !== "laeuft" || restMs <= 0) return;
    tippSperre.current = Date.now() + TIPP_SPERRE_MS;
    setLauf((l) => {
      if (!l || frageId in l.antworten) return l;
      return { ...l, antworten: { ...l.antworten, [frageId]: wert } };
    });
    zwischenstandSenden(laufRef);
  };

  // Vorzeitig beenden (nach Rueckfrage): wie Zeitablauf – die bisherigen
  // Antworten werden gewertet, der Versuch ist damit verbraucht. Neu spielen
  // geht nur ueber "Zuruecksetzen" in der Quiz-Verwaltung (auch fuer Tests).
  const quizBeenden = () => {
    setBeendenFragen(false);
    if (phase !== "laeuft") return;
    vorlesenStoppen();
    setLauf((l) => (l ? { ...l, beendet: true } : l));
    setPhase("senden");
  };

  // --- PIN-geschuetzte Aktionen --------------------------------------------
  const pinBestaetigt = async (pin) => {
    setPinLaedt(true);
    setPinFehler("");
    try {
      if (pinZweck === "team") {
        const teams = await post(`/quiz/${station.station_number}/teams`, { pin });
        setTeamListe({ pin, teams });
        setTeamSuche("");
      } else {
        await post(`/quiz/${station.station_number}/pin`, { pin });
        setEinstellungen(true);
      }
      setPinZweck(null);
    } catch (err) {
      setPinFehler(err.message || "PIN konnte nicht geprüft werden.");
    } finally {
      setPinLaedt(false);
    }
  };

  const einstellungenOeffnen = () => {
    // Ohne PIN (noch nicht vergeben) oder ohne Station gibt es nichts zu schuetzen.
    if (!station || !station.pin_gesetzt) setEinstellungen(true);
    else {
      setPinFehler("");
      setPinZweck("einstellungen");
    }
  };

  const vollbild = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  // =========================================================================
  // Darstellung

  const rahmen = (inhalt, { kopf = true } = {}) => (
    <div className="min-h-[calc(100dvh-2rem)] flex flex-col text-white select-none">
      {kopf && (
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold text-yellow-400">
              {station ? `Station ${station.station_number}` : "Quiz"}
            </div>
            <div className="text-xl font-semibold">{station?.station_name || "Quiz-Tablet"}</div>
          </div>
          <button onClick={einstellungenOeffnen} className="p-3 rounded-full text-gray-400 hover:text-white hover:bg-secondary-800" aria-label="Einstellungen">
            <Settings size={26} />
          </button>
        </div>
      )}
      <div className="flex-1 flex flex-col">{inhalt}</div>
    </div>
  );

  const fehlerBox = meldung && (
    <div className="mt-4 p-4 rounded-xl bg-red-900/70 text-red-100 text-lg text-center">{meldung}</div>
  );

  let inhalt;

  if (!stationen) {
    inhalt = rahmen(
      <div className="flex-1 flex flex-col items-center justify-center gap-4">
        {ladeFehler ? (
          <>
            <p className="text-red-300 text-lg">{ladeFehler}</p>
            <Button onClick={stationenLaden}><RefreshCw size={18} className="mr-2" /> Erneut versuchen</Button>
          </>
        ) : (
          <Loader2 className="animate-spin" size={40} />
        )}
      </div>
    );
  } else if (!station && phase !== "laeuft" && phase !== "senden") {
    inhalt = rahmen(
      <div className="max-w-xl mx-auto w-full space-y-3">
        <h1 className="text-2xl font-bold mb-2">Welche Quiz-Station ist dieses Tablet?</h1>
        {stationen.length === 0 && (
          <p className="text-gray-400">
            Es gibt noch keine Quiz-Station. Lege unter „Stationen“ eine Station vom Typ „Quiz am Tablet“ an.
          </p>
        )}
        {stationen.map((s) => (
          <button
            key={s.station_number}
            onClick={() => setStationNr(s.station_number)}
            className="w-full text-left p-4 rounded-xl bg-secondary-800 hover:bg-secondary-700"
          >
            <div className="text-xs font-bold text-yellow-400">Station {s.station_number}</div>
            <div className="text-lg font-semibold">{s.station_name}</div>
            <div className="text-sm text-gray-400">
              {s.allow_groups !== "Kinder" && `${s.fragen_erwachsene} Fragen Erwachsene`}
              {s.allow_groups === "Alle" && " · "}
              {s.allow_groups !== "Erwachsene" && `${s.fragen_kinder} Fragen Kinder`}
            </div>
          </button>
        ))}
      </div>
    );
  } else if (phase === "bereit") {
    inhalt = rahmen(
      <div className="flex-1 flex flex-col items-center justify-center gap-6 text-center">
        <h1 className="text-4xl sm:text-5xl font-extrabold">Bereit fürs Quiz?</h1>
        <p className="text-xl text-gray-300 max-w-xl">
          Haltet den QR-Code von eurem Laufzettel vor die Kamera.
        </p>
        {station.allow_groups !== "Kinder" && station.fragen_erwachsene === 0 && (
          <p className="text-yellow-300">Achtung: Für Erwachsene sind noch keine Fragen hinterlegt.</p>
        )}
        {station.allow_groups !== "Erwachsene" && station.fragen_kinder === 0 && (
          <p className="text-yellow-300">Achtung: Für Kinder sind noch keine Fragen hinterlegt.</p>
        )}
        <button
          onClick={() => setScanner(true)}
          disabled={arbeitet}
          className="flex items-center gap-4 px-10 py-6 rounded-2xl bg-yellow-400 text-black text-2xl font-bold shadow-lg active:scale-95 transition disabled:opacity-60"
        >
          {arbeitet ? <Loader2 className="animate-spin" size={34} /> : <QrCode size={34} />}
          Laufzettel scannen
        </button>
        {station.pin_gesetzt && (
          <button
            onClick={() => {
              setPinFehler("");
              setPinZweck("team");
            }}
            className="flex items-center gap-2 text-gray-400 hover:text-white underline underline-offset-4"
          >
            <Users size={18} /> Team ohne Laufzettel auswählen (Betreuer-PIN)
          </button>
        )}
        {fehlerBox}
      </div>
    );
  } else if (phase === "team" && anmeldung) {
    const a = anmeldung;
    const kinder = a.team.event_type === "Kinder";
    let unten;
    if (a.status === "neu") {
      unten = (
        <>
          <ul className="text-xl text-gray-200 space-y-2 text-left max-w-xl mx-auto">
            <li>• {a.fragen} Fragen, ihr habt <b>{dauerText(a.sekunden)}</b> Zeit.</li>
            <li>• Tippt die richtige Antwort an – danach kommt sofort die nächste Frage. Zurück geht nicht.</li>
            {a.abzug ? (
              <li>• <b>Falsche Antworten kosten Punkte.</b> Wenn ihr es nicht wisst: lieber „Überspringen“.</li>
            ) : (
              <li>• Jede richtige Antwort zählt einen Punkt.</li>
            )}
            {a.vorlesen ? (
              <li>• Das Tablet liest euch jede Frage vor – die Zeit läuft dabei weiter.</li>
            ) : (
              kinder && <li>• Die Fragen werden euch vorgelesen.</li>
            )}
            <li>• Ihr habt genau <b>einen</b> Versuch.</li>
          </ul>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <button
              onClick={starten}
              disabled={arbeitet}
              className="flex items-center gap-3 px-12 py-6 rounded-2xl bg-green-500 text-black text-3xl font-extrabold shadow-lg active:scale-95 transition disabled:opacity-60"
            >
              {arbeitet ? <Loader2 className="animate-spin" size={32} /> : <Timer size={32} />} Los geht's!
            </button>
            <button onClick={zurueckZumStart} className="px-6 py-6 rounded-2xl bg-secondary-700 text-xl">Abbrechen</button>
          </div>
        </>
      );
    } else if (a.status === "laeuft") {
      unten = (
        <>
          <p className="text-xl text-gray-200">
            {a.rest_ms > 0
              ? `Euer Quiz läuft bereits – noch ${uhr(a.rest_ms)} Restzeit.`
              : "Eure Zeit ist abgelaufen. Das Ergebnis wird jetzt übertragen."}
          </p>
          <div className="flex gap-4 justify-center mt-8">
            <button onClick={starten} disabled={arbeitet} className="px-10 py-5 rounded-2xl bg-green-500 text-black text-2xl font-bold disabled:opacity-60">
              {a.rest_ms > 0 ? "Weiter" : "Ergebnis übertragen"}
            </button>
            <button onClick={zurueckZumStart} className="px-6 py-5 rounded-2xl bg-secondary-700 text-xl">Abbrechen</button>
          </div>
        </>
      );
    } else {
      unten = (
        <>
          <p className="text-2xl text-gray-200">
            {a.status === "fertig"
              ? `Ihr habt das Quiz schon gespielt: ${punkteText(a.ergebnis?.punkte ?? 0)} Punkte.`
              : "Für euch ist an dieser Station schon ein Ergebnis eingetragen."}
          </p>
          <button onClick={zurueckZumStart} className="mt-8 px-10 py-5 rounded-2xl bg-secondary-700 text-2xl">Zurück</button>
        </>
      );
    }
    inhalt = rahmen(
      <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
        <div className="text-lg text-gray-400">Hallo</div>
        <h1 className="text-5xl font-extrabold text-yellow-400 break-words">{a.team.team_name}</h1>
        <div className="text-gray-400 mb-4">{a.team.event_type}</div>
        {unten}
        {fehlerBox}
      </div>
    );
  } else if (phase === "laeuft" && lauf) {
    const idx = naechsteFrage(lauf.fragen, lauf.antworten);
    const frage = lauf.fragen[idx];
    const endspurt = restMs <= ENDSPURT_MS;
    inhalt = rahmen(
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-lg text-gray-300 truncate">{lauf.team.team_name}</div>
            {/* Bewusst klein und nur mit Rueckfrage: beendet den (einzigen)
                Versuch, die bisherigen Antworten werden gewertet. */}
            <button
              onClick={() => setBeendenFragen(true)}
              className="mt-1 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-300"
            >
              <X size={14} /> Quiz beenden
            </button>
          </div>
          <div className={`font-mono font-extrabold tabular-nums text-6xl sm:text-7xl ${endspurt ? "text-red-400 animate-pulse" : "text-white"}`}>
            {uhr(restMs)}
          </div>
          <div className="text-lg text-gray-300 text-right">
            Frage {Math.min(idx + 1, lauf.fragen.length)} / {lauf.fragen.length}
          </div>
        </div>
        <div className="w-full h-2 bg-secondary-800 rounded-full mt-3 overflow-hidden">
          <div className="h-2 bg-yellow-400 transition-all" style={{ width: `${(idx / lauf.fragen.length) * 100}%` }} />
        </div>

        {frage && (
          <div className="flex-1 flex flex-col justify-center py-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-center leading-snug mb-10 break-words">{frage.text}</h2>
            {frage.typ === "wf" ? (
              <div className="grid grid-cols-2 gap-6">
                {frage.antworten.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => antworten(frage.id, i)}
                    className={`py-12 rounded-3xl text-4xl font-extrabold shadow-lg active:scale-95 transition ${
                      i === 0 ? "bg-green-600 hover:bg-green-500" : "bg-red-600 hover:bg-red-500"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {frage.antworten.map((t, i) => (
                  <button
                    key={i}
                    onClick={() => antworten(frage.id, i)}
                    className="flex items-center gap-4 p-6 rounded-2xl bg-secondary-700 hover:bg-secondary-600 text-2xl font-semibold text-left shadow active:scale-95 transition"
                  >
                    <span className="flex-none w-12 h-12 rounded-full bg-yellow-400 text-black flex items-center justify-center font-extrabold">
                      {BUCHSTABEN[i]}
                    </span>
                    <span className="break-words">{t}</span>
                  </button>
                ))}
              </div>
            )}
            {(lauf.abzug || lauf.vorlesen) && (
              <div className="mt-10 flex flex-wrap justify-center gap-6">
                {lauf.vorlesen && !geraet.stumm && (
                  <button
                    onClick={() => vorlesen(vorlesetext(frage), { tempo: laufTempo })}
                    className="flex items-center gap-3 px-10 py-6 rounded-2xl bg-sky-700 hover:bg-sky-600 text-white text-3xl font-bold shadow-lg active:scale-95 transition"
                  >
                    <Volume2 size={32} /> Nochmal vorlesen
                  </button>
                )}
                {lauf.vorlesen && (
                  <button
                    onClick={() => geraetAendern({ ...geraet, stumm: !geraet.stumm })}
                    aria-pressed={geraet.stumm}
                    className={`flex items-center gap-3 px-8 py-6 rounded-2xl text-white text-3xl font-bold shadow-lg active:scale-95 transition ${
                      geraet.stumm ? "bg-red-700 hover:bg-red-600" : "bg-secondary-700 hover:bg-secondary-600"
                    }`}
                  >
                    {geraet.stumm ? <VolumeX size={32} /> : <Volume2 size={32} />}
                    {geraet.stumm ? "Ton an" : "Stumm"}
                  </button>
                )}
                {lauf.abzug && (
                  <button
                    onClick={() => antworten(frage.id, null)}
                    className="flex items-center gap-3 px-14 py-6 rounded-2xl bg-gray-500 hover:bg-gray-400 text-white text-3xl font-bold shadow-lg active:scale-95 transition"
                  >
                    <SkipForward size={32} /> Überspringen
                  </button>
                )}
              </div>
            )}
            {/* Sprechtempo direkt am Tablet – klein gehalten, gilt fuer dieses
                Geraet und die Kategorie des Teams (auch fuer die naechsten Teams). */}
            {lauf.vorlesen && (
              <div className="mt-6 flex items-center justify-center gap-3 text-gray-400">
                <button
                  onClick={() => tempoSetzen(lauf.team.event_type, tempoSchritt(laufTempo, -1), true)}
                  disabled={laufTempo <= TEMPI[0].wert}
                  className="px-4 py-2 rounded-lg bg-secondary-800 hover:bg-secondary-700 disabled:opacity-40"
                >
                  Langsamer
                </button>
                <span className="w-32 text-center text-sm">Tempo: {tempoLabel(laufTempo)}</span>
                <button
                  onClick={() => tempoSetzen(lauf.team.event_type, tempoSchritt(laufTempo, 1), true)}
                  disabled={laufTempo >= TEMPI[TEMPI.length - 1].wert}
                  className="px-4 py-2 rounded-lg bg-secondary-800 hover:bg-secondary-700 disabled:opacity-40"
                >
                  Schneller
                </button>
              </div>
            )}
          </div>
        )}
      </div>,
      { kopf: false }
    );
  } else if (phase === "senden") {
    inhalt = rahmen(
      <div className="flex-1 flex flex-col items-center justify-center text-center gap-6">
        <h1 className="text-5xl font-extrabold">
          {lauf?.beendet ? "Quiz beendet" : lauf && lauf.ende_ms <= Date.now() ? "Zeit ist um!" : "Geschafft!"}
        </h1>
        <div className="flex items-center gap-3 text-xl text-gray-300">
          <Loader2 className="animate-spin" /> Ergebnis wird übertragen …
        </div>
        {fehlerBox}
      </div>,
      { kopf: false }
    );
  } else if (phase === "fertig" && ergebnis) {
    inhalt = rahmen(
      <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
        <h1 className="text-5xl font-extrabold">
          {ergebnis.beendet ? "Quiz beendet" : ergebnis.zeitAbgelaufen ? "Zeit ist um!" : "Geschafft!"}
        </h1>
        <div className="text-2xl text-yellow-400 font-semibold">{ergebnis.team?.team_name}</div>
        <div className="text-8xl font-extrabold tabular-nums my-4">{punkteText(ergebnis.punkte)}</div>
        <div className="text-2xl text-gray-300">Punkte</div>
        <div className="text-xl text-gray-400 mt-2">
          {ergebnis.richtig} richtig · {ergebnis.falsch} falsch · {ergebnis.offen} nicht beantwortet
        </div>
        <p className="text-gray-500 mt-2">Bitte den anderen Teams nichts verraten!</p>
        <button onClick={zurueckZumStart} className="mt-8 px-10 py-5 rounded-2xl bg-yellow-400 text-black text-2xl font-bold">
          Nächstes Team
        </button>
      </div>
    );
  } else {
    inhalt = rahmen(
      <div className="flex-1 flex items-center justify-center">
        <Button onClick={zurueckZumStart}>Zurück zum Start</Button>
      </div>
    );
  }

  const gefilterteTeams = (teamListe?.teams || []).filter((t) =>
    t.team_name.toLowerCase().includes(teamSuche.trim().toLowerCase())
  );

  return (
    <>
      {inhalt}

      <TeamQrScanner open={scanner} onClose={() => setScanner(false)} onScan={onScan} />

      {/* Rueckfrage zum vorzeitigen Beenden. "Weiterspielen" ist der grosse,
          naheliegende Knopf; die Uhr laeuft waehrenddessen weiter. */}
      <Modal
        open={beendenFragen && phase === "laeuft"}
        onClose={() => setBeendenFragen(false)}
        title="Quiz wirklich beenden?"
        className="max-w-md"
      >
        <p className="text-lg text-gray-300">
          Eure bisherigen Antworten werden gewertet. Ihr habt nur einen Versuch – danach könnt ihr nicht
          weiterspielen.
        </p>
        <div className="grid gap-3 pt-2">
          <button
            onClick={() => setBeendenFragen(false)}
            className="py-4 rounded-xl bg-green-600 hover:bg-green-500 text-xl font-bold"
          >
            Weiterspielen
          </button>
          <button onClick={quizBeenden} className="py-3 rounded-xl bg-red-700 hover:bg-red-600 text-base font-semibold">
            Ja, Quiz beenden
          </button>
        </div>
      </Modal>

      <PinDialog
        open={!!pinZweck}
        titel={pinZweck === "team" ? "Betreuer-PIN für die Teamauswahl" : "PIN für die Einstellungen"}
        onClose={() => setPinZweck(null)}
        onOk={pinBestaetigt}
        fehler={pinFehler}
        laedt={pinLaedt}
      />

      <Modal open={!!teamListe} onClose={() => setTeamListe(null)} title="Team auswählen" className="max-w-lg">
        <input
          autoFocus
          value={teamSuche}
          onChange={(e) => setTeamSuche(e.target.value)}
          placeholder="Teamname suchen …"
          className="w-full px-4 py-3 rounded-lg bg-secondary-800 border border-secondary-700 text-lg"
        />
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {gefilterteTeams.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                const pin = teamListe.pin;
                setTeamListe(null);
                anmelden({ team_id: t.id, pin });
              }}
              className="w-full text-left px-4 py-3 rounded-lg bg-secondary-800 hover:bg-secondary-700"
            >
              <span className="text-lg">{t.team_name}</span>
              <span className="ml-2 text-sm text-gray-400">{t.event_type}</span>
            </button>
          ))}
          {gefilterteTeams.length === 0 && <p className="text-gray-400">Kein Team gefunden.</p>}
        </div>
      </Modal>

      <Modal open={einstellungen} onClose={() => setEinstellungen(false)} title="Tablet-Einstellungen" className="max-w-md">
        <p className="text-sm text-gray-400">Angemeldet als {user?.username}.</p>
        {station && !station.pin_gesetzt && (
          <p className="text-sm text-yellow-300">
            Für diese Station ist keine PIN gesetzt – die Einstellungen sind ungeschützt und die manuelle
            Teamauswahl ist aus. PIN unter „Quiz“ in der Verwaltung vergeben.
          </p>
        )}
        <div className="grid gap-3">
          <Button variant="secondary" onClick={vollbild}><Maximize size={18} className="mr-2" /> Vollbild</Button>
          {/* Vorlesen: Stimme und Tempo dieses Geraets, Lautsprecher pruefen. */}
          {vorlesenVerfuegbar() ? (
            <div className="grid gap-2 rounded-lg border border-secondary-700 p-3">
              <div className="text-sm font-semibold text-gray-200">Vorlesen</div>
              <label className="text-sm text-gray-400">
                Stimme
                <select
                  value={geraet.stimme || ""}
                  onChange={(e) => geraetAendern({ ...geraet, stimme: e.target.value || null })}
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-secondary-800 border border-secondary-700 text-white"
                >
                  <option value="">Automatisch (beste verfügbare)</option>
                  {stimmen.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>
                      {v.name}
                      {v.localService ? "" : " (online)"}
                    </option>
                  ))}
                </select>
              </label>
              {stimmen.length === 0 && (
                <p className="text-xs text-yellow-300">
                  Keine deutsche Stimme gefunden – deutsches Sprachpaket in den Geräteeinstellungen installieren.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {["Erwachsene", "Kinder"].map((k) => (
                  <label key={k} className="text-sm text-gray-400">
                    Tempo {k}
                    <select
                      value={String(geraet.tempo[tempoSchluessel(k)])}
                      onChange={(e) => tempoSetzen(k, Number(e.target.value), false)}
                      className="mt-1 w-full px-3 py-2 rounded-lg bg-secondary-800 border border-secondary-700 text-white"
                    >
                      {TEMPI.map((t) => (
                        <option key={t.wert} value={String(t.wert)}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {["Erwachsene", "Kinder"].map((k) => (
                  <Button
                    key={k}
                    variant="secondary"
                    onClick={() => {
                      setVorlesenProbe({ ok: true, text: "…" });
                      vorlesenTesten(setVorlesenProbe, { tempo: geraet.tempo[tempoSchluessel(k)] });
                    }}
                  >
                    <Volume2 size={18} className="mr-2" /> Test {k}
                  </Button>
                ))}
              </div>
              {vorlesenProbe && (
                <p className={`text-sm ${vorlesenProbe.ok ? "text-gray-300" : "text-red-300"}`}>{vorlesenProbe.text}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Vorlesen: auf diesem Gerät nicht verfügbar.</p>
          )}
          {station && !station.vorlesen && !station.vorlesen_kinder && (
            <p className="text-xs text-gray-400">
              Vorlesen ist an dieser Station ausgeschaltet (Stationen → Details → Quiz-Einstellungen).
            </p>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setEinstellungen(false);
              stationenLaden();
            }}
          >
            <RefreshCw size={18} className="mr-2" /> Fragen/Einstellungen neu laden
          </Button>
          {(stationen || []).length > 1 && (
            <Button
              variant="secondary"
              disabled={phase === "laeuft" || phase === "senden"}
              onClick={() => {
                setEinstellungen(false);
                zurueckZumStart();
                setStationNr(null);
              }}
            >
              Andere Quiz-Station wählen
            </Button>
          )}
          <Button
            variant="danger"
            disabled={phase === "laeuft" || phase === "senden"}
            onClick={() => {
              setEinstellungen(false);
              logout();
            }}
          >
            <LogOut size={18} className="mr-2" /> Abmelden
          </Button>
        </div>
      </Modal>
    </>
  );
}
